import { Component, EventEmitter, Output } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { AuthError } from 'aws-amplify/auth';
import { Router } from '@angular/router';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-login-signup',
  templateUrl: './login-signup.component.html',
  styleUrls: ['./login-signup.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    MatInputModule,
    MatFormFieldModule,
    MatButtonModule,
    MatTabsModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatCheckboxModule,
    MatSnackBarModule,
  ],
})
export class LoginSignupComponent {
  @Output() loginBoxHide = new EventEmitter<void>();

  isLoading = false;
  errorMessage = '';
  successMessage = '';
  currentTabIndex = 0;
  showVerificationModal = false;
  pendingEmail = '';
  isResending = false;

  showNewPasswordModal = false;

  hidePassword = true;
  hideSignupPassword = true;
  hideConfirmPassword = true;
  hideNewPassword = true;
  hideConfirmNewPassword = true;

  loginForm = new FormGroup({
    email: new FormControl('', [Validators.required]),
    password: new FormControl('', [Validators.required]),
    rememberMe: new FormControl(false),
  });

  signupForm = new FormGroup(
    {
      email: new FormControl('', [Validators.required]),
      password: new FormControl('', [Validators.required, Validators.minLength(8)]),
      confirmPassword: new FormControl('', [Validators.required]),
      agreeToTerms: new FormControl(false, [Validators.requiredTrue]),
    },
    { validators: this.passwordMatchValidator }
  );

  verificationForm = new FormGroup({
    code: new FormControl('', [
      Validators.required,
      Validators.minLength(6),
      Validators.pattern(/^\d{6}$/),
    ]),
  });

  newPasswordForm = new FormGroup(
    {
      newPassword: new FormControl('', [Validators.required, Validators.minLength(8)]),
      confirmNewPassword: new FormControl('', [Validators.required]),
    },
    { validators: this.newPasswordMatchValidator }
  );

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private auth: AuthService
  ) {}

  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password');
    const confirmPassword = form.get('confirmPassword');

    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ ...(confirmPassword.errors ?? {}), mismatch: true });
      return { mismatch: true };
    }

    if (confirmPassword?.errors?.['mismatch']) {
      const { mismatch: _mismatch, ...rest } = confirmPassword.errors;
      confirmPassword.setErrors(Object.keys(rest).length ? rest : null);
    }

    return null;
  }

  newPasswordMatchValidator(form: FormGroup) {
    const newPassword = form.get('newPassword');
    const confirmNewPassword = form.get('confirmNewPassword');

    if (newPassword && confirmNewPassword && newPassword.value !== confirmNewPassword.value) {
      confirmNewPassword.setErrors({ ...(confirmNewPassword.errors ?? {}), mismatch: true });
      return { mismatch: true };
    }

    if (confirmNewPassword?.errors?.['mismatch']) {
      const { mismatch: _mismatch, ...rest } = confirmNewPassword.errors;
      confirmNewPassword.setErrors(Object.keys(rest).length ? rest : null);
    }

    return null;
  }

  onTabChange(event: { index: number }) {
    this.currentTabIndex = event.index;
    this.clearMessages();
  }

  clearMessages() {
    this.errorMessage = '';
    this.successMessage = '';
  }

  async signIn() {
    if (this.loginForm.invalid) {
      this.markFormGroupTouched(this.loginForm);
      return;
    }

    this.isLoading = true;
    this.clearMessages();

    try {
      const { email, password } = this.loginForm.value;
      const result = await this.auth.signIn(email!, password!);

      if (result.isSignedIn) {
        await this.completeSignIn(email!);
      } else if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        this.pendingEmail = email!;
        this.showNewPasswordModal = true;
        this.errorMessage = 'Your password needs to be changed before you can continue.';
        this.showSnackBar('Please set a new password to continue', 'info');
      } else {
        this.pendingEmail = email!;
        this.showVerificationModal = true;
        this.errorMessage = 'Please verify your email address to continue.';
        this.showSnackBar('Please check your email for verification code', 'info');
      }
    } catch (error) {
      if (
        error instanceof Error &&
        (error.message.includes('UserNotConfirmedException') ||
          error.message.includes('not confirmed') ||
          error.name === 'UserNotConfirmedException')
      ) {
        this.pendingEmail = this.loginForm.get('email')?.value || '';
        this.showVerificationModal = true;
        this.errorMessage = 'Please verify your email address to continue.';
        this.showSnackBar('Please check your email for verification code', 'info');
      } else if (error instanceof Error && error.message.startsWith('Email')) {
        this.errorMessage = error.message;
        this.showSnackBar(this.errorMessage, 'error');
      } else {
        this.handleAuthError(error as AuthError);
      }
    } finally {
      this.isLoading = false;
    }
  }

  async signUp() {
    if (this.signupForm.invalid) {
      this.markFormGroupTouched(this.signupForm);
      return;
    }

    this.isLoading = true;
    this.clearMessages();

    try {
      const { email, password } = this.signupForm.value;
      const result = await this.auth.signUp(email!, password!);

      if (result.isSignUpComplete) {
        this.successMessage = 'Account created successfully! Please check your email for verification.';
        this.showSnackBar('Account created! Please verify your email.', 'success');
        setTimeout(() => {
          this.currentTabIndex = 0;
          this.signupForm.reset();
        }, 1500);
      } else {
        this.pendingEmail = email!;
        this.showVerificationModal = true;
        this.successMessage = 'Verification code sent to your email!';
      }
    } catch (error) {
      if (error instanceof Error && error.message.startsWith('Email')) {
        this.errorMessage = error.message;
        this.showSnackBar(this.errorMessage, 'error');
      } else {
        this.handleAuthError(error as AuthError);
      }
    } finally {
      this.isLoading = false;
    }
  }

  async forgotPassword(event: Event) {
    event.preventDefault();

    const email = this.loginForm.get('email')?.value;
    if (!email) {
      this.showSnackBar('Please enter your email address first', 'warning');
      return;
    }

    try {
      await this.auth.resetPassword(email);
      this.showSnackBar('Password reset email sent! Check your inbox.', 'success');
    } catch (error) {
      if (error instanceof Error && error.message.startsWith('Email')) {
        this.errorMessage = error.message;
        this.showSnackBar(this.errorMessage, 'error');
      } else {
        this.handleAuthError(error as AuthError);
      }
    }
  }

  async confirmSignUp() {
    if (this.verificationForm.invalid) {
      this.markFormGroupTouched(this.verificationForm);
      return;
    }

    this.isLoading = true;
    this.clearMessages();

    try {
      const { code } = this.verificationForm.value;
      const result = await this.auth.confirmSignUp(this.pendingEmail, code!);

      if (result.isSignUpComplete) {
        this.successMessage = 'Email verified successfully!';
        this.showSnackBar('Email verified successfully!', 'success');

        const password = this.loginForm.get('password')?.value;
        if (this.pendingEmail && password) {
          try {
            const signInResult = await this.auth.signIn(this.pendingEmail, password);
            if (signInResult.isSignedIn) {
              try {
                await this.auth.processPendingGroupAssignments(this.pendingEmail);
              } catch (error) {
                console.warn('Failed to process group assignments:', error);
              }
              this.showVerificationModal = false;
              this.loginBoxHide.emit();
              await this.navigateAfterLogin();
              return;
            }
          } catch (error) {
            console.error('Auto sign-in after verification failed:', error);
          }
        }

        this.showVerificationModal = false;
        this.currentTabIndex = 0;
        this.verificationForm.reset();
        this.signupForm.reset();
      }
    } catch (error) {
      this.handleAuthError(error as AuthError);
    } finally {
      this.isLoading = false;
    }
  }

  async resendVerificationCode() {
    if (!this.pendingEmail) {
      this.showSnackBar('No pending email found', 'error');
      return;
    }

    this.isResending = true;
    this.clearMessages();

    try {
      await this.auth.resendSignUpCode(this.pendingEmail);
      this.successMessage = 'Verification code resent! Check your email.';
      this.showSnackBar('Verification code resent!', 'success');
    } catch (error) {
      this.handleAuthError(error as AuthError);
    } finally {
      this.isResending = false;
    }
  }

  goBackToSignup() {
    this.showVerificationModal = false;
    this.currentTabIndex = 1;
    this.clearMessages();
    this.verificationForm.reset();
  }

  goBackToLogin() {
    this.showVerificationModal = false;
    this.currentTabIndex = 0;
    this.clearMessages();
    this.verificationForm.reset();
  }

  async submitNewPassword() {
    if (this.newPasswordForm.invalid) {
      this.markFormGroupTouched(this.newPasswordForm);
      return;
    }

    this.isLoading = true;
    this.clearMessages();

    try {
      const { newPassword } = this.newPasswordForm.value;
      const result = await this.auth.completeNewPassword(newPassword!);

      if (result.isSignedIn) {
        this.showNewPasswordModal = false;
        this.newPasswordForm.reset();
        await this.completeSignIn(this.pendingEmail);
      }
    } catch (error) {
      this.handleAuthError(error as AuthError);
    } finally {
      this.isLoading = false;
    }
  }

  goBackFromNewPassword() {
    this.showNewPasswordModal = false;
    this.currentTabIndex = 0;
    this.clearMessages();
    this.newPasswordForm.reset();
  }

  private async completeSignIn(email: string) {
    this.successMessage = 'Welcome back! Signing you in...';
    this.showSnackBar('Successfully signed in!', 'success');

    try {
      await this.auth.processPendingGroupAssignments(email);
    } catch (error) {
      console.warn('Failed to process group assignments:', error);
    }

    setTimeout(() => {
      this.loginBoxHide.emit();
      void this.navigateAfterLogin();
    }, 800);
  }

  private async navigateAfterLogin() {
    try {
      const user = await this.auth.getCurrentUser();
      const groups = await this.auth.getUserGroupsWithFallback(user.username);
      await this.router.navigateByUrl(this.auth.resolvePostLoginRoute(groups));
    } catch (error) {
      console.error('Error navigating after login:', error);
      await this.router.navigate(['/']);
    }
  }

  private handleAuthError(error: AuthError) {
    switch (error.name) {
      case 'UserNotFoundException':
        this.errorMessage = 'No account found with this email address.';
        break;
      case 'NotAuthorizedException':
        this.errorMessage = 'Incorrect email or password.';
        break;
      case 'UserNotConfirmedException':
        this.errorMessage = 'Please verify your email address before signing in.';
        break;
      case 'InvalidPasswordException':
        this.errorMessage = 'Password does not meet requirements.';
        break;
      case 'UsernameExistsException':
        this.errorMessage = 'An account with this email already exists.';
        break;
      case 'InvalidParameterException':
        this.errorMessage = 'Please enter a valid email address (e.g., user@example.com).';
        break;
      case 'TooManyRequestsException':
        this.errorMessage = 'Too many attempts. Please try again later.';
        break;
      case 'CodeMismatchException':
        this.errorMessage = 'Invalid verification code. Please check and try again.';
        break;
      case 'ExpiredCodeException':
        this.errorMessage = 'Verification code has expired. Please request a new one.';
        break;
      case 'LimitExceededException':
        this.errorMessage = 'Too many verification attempts. Please try again later.';
        break;
      default:
        this.errorMessage = error.message || 'An error occurred. Please try again.';
    }

    this.showSnackBar(this.errorMessage, 'error');
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((key) => {
      formGroup.get(key)?.markAsTouched();
    });
  }

  private showSnackBar(message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: [`snackbar-${type}`],
    });
  }
}
